import { storableError } from '../../util/errors';
import { withPrivateListingsExcluded } from '../../util/publicListingQuery';
import { filterOutTutulistListings, filterOutTutulistSellers } from '../../util/tutulist';
import { queryUsers } from '../../util/api';
import { createListingSearchParams, querySearchPageListings } from '../SearchPage/SearchPage.duck';
import { NEWEST_SORT } from '../../util/listingSort';

const RECENTLY_ADDED_PAGE_SIZE = 50;
export const FEATURED_SELLERS_COUNT = 8;
const SHOP_PREVIEW_IMAGE_COUNT = 3;

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/LandingPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/LandingPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_POPULAR_LISTINGS_SUCCESS = 'app/LandingPage/SEARCH_POPULAR_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/LandingPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_FEATURED_REQUEST = 'app/LandingPage/SEARCH_FEATURED_REQUEST';
export const SEARCH_FEATURED_SUCCESS = 'app/LandingPage/SEARCH_FEATURED_SUCCESS';
export const SEARCH_FEATURED_ERROR = 'app/LandingPage/SEARCH_FEATURED_ERROR';
export const FETCH_SHOP_PREVIEWS_SUCCESS = 'app/LandingPage/FETCH_SHOP_PREVIEWS_SUCCESS';
export const SET_LOADED_ON_SERVER = 'app/LandingPage/SET_LOADED_ON_SERVER';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  searchMapListingIds: [],
  searchMapListingsError: null,
  featuredSellers: [],
  featuredSellersInProgress: false,
  featuredSellersError: null,
  // { [sellerId]: { totalItems, images: [url] } }
  shopPreviews: {},
  popularPageResultIds: [],
  // True when the server loaded this page's data (see loadData)
  loadedOnServer: false,
};

const resultIds = data => data.data.map(l => l.id);

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_FEATURED_REQUEST:
      return {
        ...state,
        featuredSellersInProgress: true,
        featuredSellersError: null,
      };
    case SEARCH_FEATURED_SUCCESS:
      return {
        ...state,
        featuredSellers: payload?.data,
        featuredSellersInProgress: false,
        featuredSellersError: null,
      };
    case SEARCH_FEATURED_ERROR:
      return {
        ...state,
        featuredSellersInProgress: false,
        featuredSellersError: payload,
      };
    case SET_LOADED_ON_SERVER:
      return { ...state, loadedOnServer: payload };
    case FETCH_SHOP_PREVIEWS_SUCCESS:
      return { ...state, shopPreviews: { ...state.shopPreviews, ...payload } };
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_POPULAR_LISTINGS_SUCCESS:
      return {
        ...state,
        popularPageResultIds: payload?.data ? resultIds(payload?.data) : [],
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchPopularListingsSuccess = response => ({
  type: SEARCH_POPULAR_LISTINGS_SUCCESS,
  payload: { data: response?.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchFeaturedSuccess = response => ({
  type: SEARCH_FEATURED_SUCCESS,
  payload: { data: response },
});

export const searchFeaturedRequest = () => ({
  type: SEARCH_FEATURED_REQUEST,
});

export const searchFeaturedError = e => ({
  type: SEARCH_FEATURED_ERROR,
  error: true,
  payload: e,
});

export const setLoadedOnServer = loaded => ({
  type: SET_LOADED_ON_SERVER,
  payload: loaded,
});

export const fetchShopPreviewsSuccess = previews => ({
  type: FETCH_SHOP_PREVIEWS_SUCCESS,
  payload: previews,
});

// Featured (non Shop Small) sellers shown on the landing page, in display order
export const selectFeaturedSellers = sellers =>
  Array.isArray(sellers)
    ? filterOutTutulistSellers(sellers)
        .filter(seller => !seller?.isSmall)
        .slice(0, FEATURED_SELLERS_COUNT)
    : [];

const getUuid = id => id?.uuid || id;

// Loads a few listing photos and the public item count for each featured seller's shop card.
// A failed seller just shows its card without photos.
export const fetchShopPreviews = sellers => (dispatch, _getState, sdk) => {
  const fetchOne = seller => {
    const sellerId = getUuid(seller?.id);
    return sdk.listings
      .query(
        withPrivateListingsExcluded({
          authorId: sellerId,
          // In-stock only, so the count and photos match what the seller's store shows
          minStock: 1,
          perPage: SHOP_PREVIEW_IMAGE_COUNT,
          include: ['images'],
          'fields.image': ['variants.square-small', 'variants.square-small2x'],
          'limit.images': 1,
        })
      )
      .then(response => {
        const { data = [], included = [], meta = {} } = response?.data || {};
        const imagesById = included
          .filter(entity => entity.type === 'image')
          .reduce((acc, image) => ({ ...acc, [getUuid(image.id)]: image }), {});
        const images = filterOutTutulistListings(data)
          .map(listing => {
            const imageId = getUuid(listing?.relationships?.images?.data?.[0]?.id);
            const variants = imagesById[imageId]?.attributes?.variants || {};
            return (variants['square-small2x'] || variants['square-small'])?.url;
          })
          .filter(Boolean);
        return [sellerId, { totalItems: meta.totalItems || 0, images }];
      })
      .catch(() => null);
  };

  return Promise.all(sellers.map(fetchOne)).then(results => {
    const previews = Object.fromEntries(results.filter(Boolean));
    dispatch(fetchShopPreviewsSuccess(previews));
    return previews;
  });
};

export const queryFeaturedUsers = () => async dispatch => {
  dispatch(searchFeaturedRequest());

  try {
    const response = (await queryUsers()) || {};
    const { sellersData = [], shopPreviews } = response;
    dispatch(searchFeaturedSuccess(sellersData));
    // The server sends the shop previews with the sellers (one shared, cached copy)
    if (shopPreviews) {
      dispatch(fetchShopPreviewsSuccess(shopPreviews));
      return sellersData;
    }
    // Browser only: during SSR these would finish after the HTML is sent and be thrown away
    if (typeof window === 'undefined') {
      return sellersData;
    }
    Promise.resolve(dispatch(fetchShopPreviews(selectFeaturedSellers(sellersData)))).catch(
      () => {}
    );
    return sellersData;
  } catch (error) {
    dispatch(searchFeaturedError(storableError(error)));
    // eslint-disable-next-line no-console
    console.error(error, 'error while fetching featured users on landing page');
    return [];
  }
};

export const searchListings = ({ searchParams, config }) => (dispatch, _getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));

  return querySearchPageListings(
    { searchParams, config },
    {
      dispatch,
      rejectWithValue: error => Promise.reject(error),
      extra: sdk,
    }
  )
    .then(async response => {
      dispatch(searchListingsSuccess(response));
      const featuredUsers = Promise.resolve(dispatch(queryFeaturedUsers())).catch(() => {});
      // The server waits for the featured shops so they're in the page it sends (the seller list
      // is cached on the server, so this is quick); the browser shows them when they arrive
      if (typeof window === 'undefined') {
        await featuredUsers;
      }

      return response;
    })
    .catch(error => {
      dispatch(searchListingsError(error));
      throw error;
    });
};

// The server renders the landing page with the same data the browser would fetch right after,
// and every fetch is billed. When the server loaded it and the visitor isn't logged in, the
// browser keeps the server's data.
const canReuseServerData = (state, sdk) => {
  const { loadedOnServer, featuredSellers } = state.LandingPage || {};
  if (typeof window === 'undefined' || !loadedOnServer || !featuredSellers?.length) {
    return Promise.resolve(false);
  }
  return Promise.resolve(sdk.authInfo())
    .then(authInfo => authInfo?.isAnonymous !== false)
    .catch(() => false);
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  return canReuseServerData(getState(), sdk).then(reuse => {
    dispatch(setLoadedOnServer(false));
    if (reuse) {
      return Promise.resolve();
    }
    return dispatch(loadLandingPageData(params, search, config)).then(response => {
      if (typeof window === 'undefined') {
        dispatch(setLoadedOnServer(true));
      }
      return response;
    });
  });
};

const loadLandingPageData = (_params, _search, config) => {
  const searchParams = createListingSearchParams({
    queryParams: {
      page: 1,
      sort: NEWEST_SORT,
    },
    config,
    pageOverride: 1,
    perPageOverride: RECENTLY_ADDED_PAGE_SIZE,
  });

  return searchListings({
    searchParams,
    config,
  });
};
